import { useState } from 'react';
import PropTypes from 'prop-types';
import classes from './Forecast.module.css';
import Tabs, { Tab } from 'Components/Molecules/Tabs/Tabs';
import ForecastValueTransactions from './ForecastValueTransactions';
import { useTranslation } from 'react-i18next';
import ForecastValueInvoices from './ForecastValueInvoices';
import { useSnackbar } from 'Components/Molecules/SnackbarProvider/SnackbarProvider';
import { trackError } from 'Utils/errorMonitoring';
import { useQueryClient } from '@tanstack/react-query';
import { deleteForecastValue } from 'Services/forecasts';
import useDrawerStack from 'Hooks/useDrawerStack';
import { DRAWER_STACK } from 'Utils/constants';
import ForecastValueSummary from './ForecastValueSummary';

const TAB_NAMES = {
    TRANSACTIONS: 'liquidated',
    INVOICES: 'pending',
};

function getInitialTab(cell) {
    // The goal is not a tab anymore, it lives in the summary on top
    const tab = cell?.data_origin ?? TAB_NAMES.INVOICES;

    return Object.values(TAB_NAMES).includes(tab) ? tab : TAB_NAMES.INVOICES;
}

export default function ForecastValue(props) {
    const {
        forecastId,
        onClose,
        cell,
        title,
        periodicity,
        predictedOn,
        hasPeriodicityView,
        isConsolidatedMatrix,
        onRestorePeriodicity,
    } = props;

    const [processing, setProcessing] = useState(false);
    const [activedTab, setActivedTab] = useState(() => getInitialTab(cell));

    const { t } = useTranslation('forecasts');
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const { open } = useDrawerStack();

    const hasForecastValue = !!cell?.filters?.forecast_value_id;

    const handleOpenGoal = () => {
        open(DRAWER_STACK.FORECAST_VALUE_GOAL, {
            title: `${t('cell.goal')} · ${title}`,
            cell,
            forecastId,
            periodicity,
            predictedOn,
            hasPeriodicityView,
            isConsolidatedMatrix,
            onRestorePeriodicity,
        });
    };

    const handleDeleteForecastValue = async () => {
        setProcessing(true);
        try {
            await deleteForecastValue(forecastId, cell?.filters?.forecast_value_id);
            await queryClient.invalidateQueries({ queryKey: ['forecasts', forecastId] });
        } catch (error) {
            trackError(error);
            enqueueSnackbar(t('error-deleting-forecast-value-title'), {
                variant: 'error',
                description: t('error-deleting-forecast-value-description'),
            });
        } finally {
            onClose();
            setProcessing(false);
        }
    };

    return (
        <div className="pt-2 min-h-full relative flex flex-col">
            <div className="mb-6 opacity-50 text-xs empty:hidden">{cell?.period_label}</div>

            <ForecastValueSummary
                cell={cell}
                onOpenGoal={handleOpenGoal}
                onDeleteGoal={hasForecastValue ? handleDeleteForecastValue : null}
                isDeletingGoal={processing}
            />

            <div className={classes.tabs}>
                <Tabs value={activedTab} onChange={setActivedTab}>
                    <Tab value={TAB_NAMES.TRANSACTIONS}>{t('tab-liquidated')}</Tab>
                    <Tab value={TAB_NAMES.INVOICES}>{t('tab-pending')}</Tab>
                </Tabs>
            </div>

            {activedTab === TAB_NAMES.TRANSACTIONS && <ForecastValueTransactions cell={cell} forecastId={forecastId} />}

            {activedTab === TAB_NAMES.INVOICES && (
                <ForecastValueInvoices cell={cell} forecastId={forecastId} periodicity={periodicity} />
            )}
        </div>
    );
}

ForecastValue.propTypes = {
    onClose: PropTypes.func.isRequired,
    forecastId: PropTypes.string.isRequired,
    onRestorePeriodicity: PropTypes.func.isRequired,
    cell: PropTypes.object,
    title: PropTypes.string,
    periodicity: PropTypes.string,
    predictedOn: PropTypes.string,
    hasPeriodicityView: PropTypes.bool,
    isConsolidatedMatrix: PropTypes.bool,
};

ForecastValue.drawerStackProps = {
    size: 'lg',
};
